/**
 * ГиперЧАТ — берём у него только распознавание речи («Текстом» у записанного голосового).
 *
 * Тот же вызов, что в ГиперЧАТе (chat-core/src/api.ts → dictate): POST {CHAT_API_URL}/dictate,
 * сырое тело записи, в ответ { text, language }. Сервер: GiperCHAT/server/src/domains/media/http/router.ts.
 *
 * Допуск: сервер проверяет пропуск Supabase (verifyPass → тот же JWT-секрет, что у остальных служб UNIA),
 * поэтому пропуск Saleus принимается как есть; организацию подсказываем заголовком X-Org-Id (иначе берётся
 * из пропуска). Междоменное разрешение (entry/api.ts): *.unia.one, capacitor://localhost, https://localhost.
 * Человек должен состоять в какой-нибудь организации ГиперЧАТа — иначе сервер ответит 403.
 * Серверной правки не нужно.
 */
import config from '@/lib/config';
import { getFreshAccessToken } from '@/lib/authToken';
import { ApiError, fetchWithTimeout } from '@/lib/http';
import { useProjectStore } from '@/store/project';

/** Тексты — дословно из ГиперЧАТа (Composer.tsx, takeToText). */
export const DICTATE_EMPTY = 'В записи не распознано ни слова. Её можно отправить голосом.';
export const DICTATE_FAILED = 'Не удалось распознать запись. Её можно отправить голосом.';

interface ChatErrorBody { error?: { message?: string } }

export async function dictate(audio: Blob): Promise<string> {
  const token = await getFreshAccessToken();
  if (!token) throw new ApiError(401, 'Нет действующего входа в UNIA');
  const st = useProjectStore.getState();
  const orgId = st.projects.find((p) => p.id === st.activeProjectId)?.org_id;
  let res: Response;
  try {
    res = await fetchWithTimeout(`${config.CHAT_API_URL}/dictate`, {
      method: 'POST',
      body: audio,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': audio.type || 'application/octet-stream',
        ...(orgId ? { 'X-Org-Id': orgId } : {}),
      },
    }, 90_000);
  } catch {
    throw new Error(DICTATE_FAILED);
  }
  if (!res.ok) {
    let message = DICTATE_FAILED;
    try {
      const body = (await res.json()) as ChatErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch { /* тела нет */ }
    throw new ApiError(res.status, message);
  }
  const body = (await res.json()) as { text?: string };
  return (body.text ?? '').trim();
}
